import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, Filter, QrCode, FileText, Copy, Download, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import CreditCardFields from '@/components/event/CreditCardFields';
import { encryptCardForPagSeguro } from '@/lib/pagseguro';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Store = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [pixDialogOpen, setPixDialogOpen] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [boletoDialogOpen, setBoletoDialogOpen] = useState(false);
  const [boletoData, setBoletoData] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [cardForm, setCardForm] = useState({
    holder_name: '',
    number: '',
    expiry: '',
    security_code: '',
    installments: 1,
  });
  const [checkoutData, setCheckoutData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_cpf: '',
    payment_method: 'pix',
    delivery_method: 'delivery',
    delivery_address: '',
    delivery_number: '',
    delivery_complement: '',
    delivery_neighborhood: '',
    delivery_city: '',
    delivery_state: '',
    delivery_zipcode: '',
    // Billing data
    usar_dados_faturamento: false,
    faturamento_tipo: 'pf',
    faturamento_razao_social: '',
    faturamento_cnpj: '',
    faturamento_nome: '',
    faturamento_cpf: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState(['all']);

  // CPF Mask
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  // Lista de estados brasileiros
  const ESTADOS_BRASIL = [
    { sigla: 'AC', nome: 'Acre' },
    { sigla: 'AL', nome: 'Alagoas' },
    { sigla: 'AP', nome: 'Amapá' },
    { sigla: 'AM', nome: 'Amazonas' },
    { sigla: 'BA', nome: 'Bahia' },
    { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' },
    { sigla: 'ES', nome: 'Espírito Santo' },
    { sigla: 'GO', nome: 'Goiás' },
    { sigla: 'MA', nome: 'Maranhão' },
    { sigla: 'MT', nome: 'Mato Grosso' },
    { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' },
    { sigla: 'PA', nome: 'Pará' },
    { sigla: 'PB', nome: 'Paraíba' },
    { sigla: 'PR', nome: 'Paraná' },
    { sigla: 'PE', nome: 'Pernambuco' },
    { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' },
    { sigla: 'RN', nome: 'Rio Grande do Norte' },
    { sigla: 'RS', nome: 'Rio Grande do Sul' },
    { sigla: 'RO', nome: 'Rondônia' },
    { sigla: 'RR', nome: 'Roraima' },
    { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' },
    { sigla: 'SE', nome: 'Sergipe' },
    { sigla: 'TO', nome: 'Tocantins' },
  ];

  // CNPJ Mask
  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  // Phone Mask
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers.length ? `(${numbers}` : '';
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  // CEP Mask
  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  const handleMaskedInput = (e, formatter, fieldName) => {
    const formatted = formatter(e.target.value);
    setCheckoutData(prev => ({ ...prev, [fieldName]: formatted }));
  };

  const filterProducts = useCallback(() => {
    if (selectedCategory === 'all') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(product => product.category === selectedCategory));
    }
  }, [products, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/product-categories`);
      const categoryNames = response.data.map(cat => cat.name);
      setCategories(['all', ...categoryNames]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?status=active`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [selectedCategory, products, filterProducts]);

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
      }]);
    }
    toast({
      title: 'Adicionado ao carrinho',
      description: `${product.name} foi adicionado ao seu carrinho.`,
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item =>
      item.product_id === productId
        ? { ...item, quantity: Math.max(0, item.quantity + delta) }
        : item
    ).filter(item => item.quantity > 0));
  };

  const getTotalAmount = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Prepare shipping address for PagSeguro
      const shippingAddress = checkoutData.delivery_method === 'delivery' ? {
        street: checkoutData.delivery_address,
        number: checkoutData.delivery_number,
        complement: checkoutData.delivery_complement,
        neighborhood: checkoutData.delivery_neighborhood,
        city: checkoutData.delivery_city,
        state: checkoutData.delivery_state,
        zip_code: checkoutData.delivery_zipcode
      } : null;

      const encryptedCard = checkoutData.payment_method === 'credit_card'
        ? await encryptCardForPagSeguro(API, cardForm)
        : null;

      const response = await axios.post(`${API}/orders`, {
        ...checkoutData,
        card: encryptedCard,
        items: cart,
        shipping_address: shippingAddress,
      });

      toast({
        title: 'Pedido realizado!',
        description: 'Aguarde, processando pagamento...',
      });

      // Show PIX dialog or boleto dialog based on payment method
      if (checkoutData.payment_method === 'pix') {
        setPixData({
          pix_code: response.data.pix_qr_code,
          qr_code_url: response.data.pix_qr_code_image,
          amount: response.data.total_amount,
          order_id: response.data.order_id,
        });
        setOrderId(response.data.order_id);
        setPixDialogOpen(true);
      } else if (checkoutData.payment_method === 'boleto') {
        setBoletoData({
          barcode: response.data.boleto_barcode,
          formatted_barcode: response.data.boleto_formatted_barcode || response.data.boleto_barcode,
          due_date: response.data.boleto_due_date,
          pdf_url: response.data.boleto_pdf,
          png_url: response.data.boleto_image,
          amount: response.data.total_amount,
          order_id: response.data.order_id,
        });
        setOrderId(response.data.order_id);
        setBoletoDialogOpen(true);
        toast({
          title: 'Boleto gerado!',
          description: 'Confira os dados do boleto abaixo.',
        });
      } else if (checkoutData.payment_method === 'credit_card') {
        setOrderId(response.data.order_id);
        setShowThankYou(true);
        toast({
          title: response.data.payment_status === 'paid' ? 'Pagamento aprovado!' : 'Pagamento em análise',
          description: response.data.payment_status === 'paid'
            ? 'Seu pedido foi confirmado com cartão de crédito.'
            : 'Seu pedido foi criado e o pagamento está em análise.',
        });
      }

      setCart([]);
      setCheckoutOpen(false);
      setCardForm({ holder_name: '', number: '', expiry: '', security_code: '', installments: 1 });
      setCheckoutData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_cpf: '',
        payment_method: 'pix',
        delivery_method: 'delivery',
        delivery_address: '',
        delivery_number: '',
        delivery_complement: '',
        delivery_neighborhood: '',
        delivery_city: '',
        delivery_state: '',
        delivery_zipcode: '',
        usar_dados_faturamento: false,
        faturamento_tipo: 'pf',
        faturamento_razao_social: '',
        faturamento_cnpj: '',
        faturamento_nome: '',
        faturamento_cpf: '',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || error.message || 'Não foi possível realizar o pedido.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="store-page" className="min-h-screen py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="mb-12">
        <h1 className="text-4xl md:text-6xl font-heading font-black tracking-tight uppercase text-primary mb-4">
          Loja
        </h1>
        <p className="text-base md:text-lg text-foreground/70 max-w-2xl">
          Produtos oficiais dos eventos. Bonés, camisetas e acessórios exclusivos.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1">
          <div data-testid="store-filters" className="mb-8 flex items-center gap-3 flex-wrap">
            <Filter className="w-5 h-5 text-foreground/70" />
            {categories.map((category) => (
              <Button
                key={category}
                data-testid={`filter-${category}`}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={`uppercase tracking-widest font-bold rounded-sm ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'border-2 border-primary text-primary hover:bg-primary hover:text-white'
                }`}
              >
                {category === 'all' ? 'Todos' : category}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-96 bg-secondary/50 animate-pulse rounded-lg"></div>
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  data-testid={`product-${product.id}`}
                  className="bg-card border border-border hover:border-accent transition-all duration-300 hover:shadow-xl rounded-lg overflow-hidden"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-heading font-bold uppercase mb-2 text-primary">
                      {product.name}
                    </h3>
                    <p className="text-sm text-foreground/70 mb-4">{product.description}</p>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-accent">R$ {product.price.toFixed(2)}</span>
                      <span className="text-xs uppercase tracking-widest font-bold text-primary bg-secondary px-3 py-1 rounded-sm">
                        {product.category}
                      </span>
                    </div>
                    <Button
                      data-testid={`add-to-cart-${product.id}`}
                      onClick={() => addToCart(product)}
                      disabled={product.stock === 0}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-6 py-3 rounded-sm"
                    >
                      {product.stock === 0 ? 'Esgotado' : 'Adicionar ao Carrinho'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-foreground/50 py-12">Nenhum produto encontrado.</p>
          )}
        </div>

        <div className="lg:w-80">
          <div data-testid="cart" className="bg-card border-2 border-border p-6 rounded-lg sticky top-24">
            <div className="flex items-center gap-2 mb-6">
              <ShoppingCart className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-heading font-bold uppercase text-primary">Carrinho</h2>
            </div>

            {cart.length === 0 ? (
              <p className="text-sm text-foreground/50 text-center py-8">Seu carrinho está vazio</p>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  {cart.map((item) => (
                    <div key={item.product_id} data-testid={`cart-item-${item.product_id}`} className="flex items-center justify-between pb-4 border-b border-border">
                      <div className="flex-1">
                        <p className="font-bold text-sm text-foreground">{item.name}</p>
                        <p className="text-xs text-foreground/70">R$ {item.price.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          data-testid={`decrease-${item.product_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(item.product_id, -1)}
                          className="h-8 w-8 p-0 rounded-sm"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="font-bold text-sm w-8 text-center">{item.quantity}</span>
                        <Button
                          data-testid={`increase-${item.product_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(item.product_id, 1)}
                          className="h-8 w-8 p-0 rounded-sm"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mb-6 pb-6 border-b border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold uppercase tracking-widest text-primary">Total</span>
                    <span className="text-2xl font-bold text-accent">R$ {getTotalAmount().toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  data-testid="checkout-button"
                  onClick={() => setCheckoutOpen(true)}
                  className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90 uppercase tracking-widest font-bold px-6 py-3 rounded-sm"
                >
                  Finalizar Compra
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent data-testid="checkout-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold uppercase text-primary">
              Finalizar Compra
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCheckout} className="space-y-6">
            {/* Dados Pessoais */}
            <div>
              <h3 className="text-lg font-heading font-bold uppercase text-primary mb-4">Dados Pessoais</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="customer_name" className="text-sm font-bold uppercase tracking-widest text-primary mb-2">
                    Nome Completo *
                  </Label>
                  <Input
                    id="customer_name"
                    data-testid="checkout-name"
                    type="text"
                    required
                    value={checkoutData.customer_name}
                    onChange={(e) => setCheckoutData({ ...checkoutData, customer_name: e.target.value })}
                    className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_email" className="text-sm font-bold uppercase tracking-widest text-primary mb-2">
                      Email *
                    </Label>
                    <Input
                      id="customer_email"
                      data-testid="checkout-email"
                      type="email"
                      required
                      value={checkoutData.customer_email}
                      onChange={(e) => setCheckoutData({ ...checkoutData, customer_email: e.target.value })}
                      className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                    />
                  </div>

                  <div>
                    <Label htmlFor="customer_phone" className="text-sm font-bold uppercase tracking-widest text-primary mb-2">
                      Telefone *
                    </Label>
                    <Input
                      id="customer_phone"
                      data-testid="checkout-phone"
                      type="tel"
                      required
                      placeholder="(31) 99999-9999"
                      maxLength={15}
                      value={checkoutData.customer_phone}
                      onChange={(e) => handleMaskedInput(e, formatPhone, 'customer_phone')}
                      className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_cpf" className="text-sm font-bold uppercase tracking-widest text-primary mb-2">
                      CPF *
                    </Label>
                    <Input
                      id="customer_cpf"
                      data-testid="checkout-cpf"
                      required
                      placeholder="000.000.000-00"
                      maxLength={14}
                      value={checkoutData.customer_cpf}
                      onChange={(e) => handleMaskedInput(e, formatCPF, 'customer_cpf')}
                      className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Método de Entrega */}
            <div>
              <h3 className="text-lg font-heading font-bold uppercase text-primary mb-4">Método de Entrega</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div
                  onClick={() => setCheckoutData({ ...checkoutData, delivery_method: 'delivery' })}
                  className={`border-2 p-4 rounded-lg cursor-pointer transition-all ${
                    checkoutData.delivery_method === 'delivery'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      checkoutData.delivery_method === 'delivery' ? 'border-accent' : 'border-border'
                    }`}>
                      {checkoutData.delivery_method === 'delivery' && (
                        <div className="w-3 h-3 rounded-full bg-accent"></div>
                      )}
                    </div>
                    <span className="font-bold text-primary">Entrega</span>
                  </div>
                  <p className="text-sm text-foreground/70">Receber em casa</p>
                </div>
                
                <div
                  onClick={() => setCheckoutData({ ...checkoutData, delivery_method: 'pickup_event' })}
                  className={`border-2 p-4 rounded-lg cursor-pointer transition-all ${
                    checkoutData.delivery_method === 'pickup_event'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      checkoutData.delivery_method === 'pickup_event' ? 'border-accent' : 'border-border'
                    }`}>
                      {checkoutData.delivery_method === 'pickup_event' && (
                        <div className="w-3 h-3 rounded-full bg-accent"></div>
                      )}
                    </div>
                    <span className="font-bold text-primary">Retirar no Evento</span>
                  </div>
                  <p className="text-sm text-foreground/70">Buscar pessoalmente</p>
                </div>
              </div>
            </div>

            {/* Endereço de Entrega (apenas se delivery) */}
            {checkoutData.delivery_method === 'delivery' && (
              <div>
                <h3 className="text-lg font-heading font-bold uppercase text-primary mb-4">Endereço de Entrega</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Rua *</Label>
                      <Input
                        required
                        value={checkoutData.delivery_address}
                        onChange={(e) => setCheckoutData({ ...checkoutData, delivery_address: e.target.value })}
                        className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Número *</Label>
                      <Input
                        required
                        value={checkoutData.delivery_number}
                        onChange={(e) => setCheckoutData({ ...checkoutData, delivery_number: e.target.value })}
                        className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Complemento *</Label>
                      <Input
                        required
                        placeholder="Apto, Bloco, Casa, etc."
                        value={checkoutData.delivery_complement}
                        onChange={(e) => setCheckoutData({ ...checkoutData, delivery_complement: e.target.value })}
                        className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Bairro *</Label>
                      <Input
                        required
                        value={checkoutData.delivery_neighborhood}
                        onChange={(e) => setCheckoutData({ ...checkoutData, delivery_neighborhood: e.target.value })}
                        className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Cidade *</Label>
                      <Input
                        required
                        value={checkoutData.delivery_city}
                        onChange={(e) => setCheckoutData({ ...checkoutData, delivery_city: e.target.value })}
                        className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Estado *</Label>
                      <select
                        required
                        value={checkoutData.delivery_state}
                        onChange={(e) => setCheckoutData({ ...checkoutData, delivery_state: e.target.value })}
                        className="w-full bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                      >
                        <option value="">UF</option>
                        {ESTADOS_BRASIL.map((estado) => (
                          <option key={estado.sigla} value={estado.sigla}>
                            {estado.sigla}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">CEP *</Label>
                      <Input
                        required
                        placeholder="00000-000"
                        maxLength={9}
                        value={checkoutData.delivery_zipcode}
                        onChange={(e) => handleMaskedInput(e, formatCEP, 'delivery_zipcode')}
                        className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dados de Faturamento */}
            <div className="bg-secondary/30 p-6 rounded-lg border-2 border-border">
              <div className="flex items-start gap-3 mb-4">
                <input
                  type="checkbox"
                  id="usar_dados_faturamento_store"
                  checked={checkoutData.usar_dados_faturamento}
                  onChange={(e) => setCheckoutData({ ...checkoutData, usar_dados_faturamento: e.target.checked })}
                  className="mt-1 w-5 h-5 border-2 border-border rounded accent-accent"
                />
                <Label htmlFor="usar_dados_faturamento_store" className="text-sm font-bold uppercase tracking-widest text-primary cursor-pointer">
                  Usar dados diferentes para faturamento/nota fiscal
                </Label>
              </div>
              
              {checkoutData.usar_dados_faturamento && (
                <div className="space-y-4 mt-4 pt-4 border-t border-border">
                  <div className="flex gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="faturamento_pf_store"
                        name="faturamento_tipo_store"
                        value="pf"
                        checked={checkoutData.faturamento_tipo === 'pf'}
                        onChange={(e) => setCheckoutData({ ...checkoutData, faturamento_tipo: e.target.value })}
                        className="w-4 h-4 accent-accent"
                      />
                      <Label htmlFor="faturamento_pf_store" className="text-sm cursor-pointer">Pessoa Física</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="faturamento_pj_store"
                        name="faturamento_tipo_store"
                        value="pj"
                        checked={checkoutData.faturamento_tipo === 'pj'}
                        onChange={(e) => setCheckoutData({ ...checkoutData, faturamento_tipo: e.target.value })}
                        className="w-4 h-4 accent-accent"
                      />
                      <Label htmlFor="faturamento_pj_store" className="text-sm cursor-pointer">Pessoa Jurídica</Label>
                    </div>
                  </div>
                  
                  {checkoutData.faturamento_tipo === 'pj' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Razão Social *</Label>
                        <Input
                          required={checkoutData.usar_dados_faturamento && checkoutData.faturamento_tipo === 'pj'}
                          value={checkoutData.faturamento_razao_social}
                          onChange={(e) => setCheckoutData({ ...checkoutData, faturamento_razao_social: e.target.value })}
                          className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">CNPJ *</Label>
                        <Input
                          required={checkoutData.usar_dados_faturamento && checkoutData.faturamento_tipo === 'pj'}
                          placeholder="00.000.000/0000-00"
                          maxLength={18}
                          value={checkoutData.faturamento_cnpj}
                          onChange={(e) => handleMaskedInput(e, formatCNPJ, 'faturamento_cnpj')}
                          className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Nome Completo *</Label>
                        <Input
                          required={checkoutData.usar_dados_faturamento && checkoutData.faturamento_tipo === 'pf'}
                          value={checkoutData.faturamento_nome}
                          onChange={(e) => setCheckoutData({ ...checkoutData, faturamento_nome: e.target.value })}
                          className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-bold uppercase tracking-widest text-primary mb-2">CPF *</Label>
                        <Input
                          required={checkoutData.usar_dados_faturamento && checkoutData.faturamento_tipo === 'pf'}
                          placeholder="000.000.000-00"
                          maxLength={14}
                          value={checkoutData.faturamento_cpf}
                          onChange={(e) => handleMaskedInput(e, formatCPF, 'faturamento_cpf')}
                          className="bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
                        />
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-foreground/60 mt-2">
                    * Estes dados serão utilizados para emissão de nota fiscal
                  </p>
                </div>
              )}
            </div>

            {/* Método de Pagamento */}
            <div>
              <h3 className="text-lg font-heading font-bold uppercase text-primary mb-4">Forma de Pagamento</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div
                  onClick={() => setCheckoutData({ ...checkoutData, payment_method: 'pix' })}
                  className={`border-2 p-4 rounded-lg cursor-pointer transition-all ${
                    checkoutData.payment_method === 'pix'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      checkoutData.payment_method === 'pix' ? 'border-accent' : 'border-border'
                    }`}>
                      {checkoutData.payment_method === 'pix' && (
                        <div className="w-3 h-3 rounded-full bg-accent"></div>
                      )}
                    </div>
                    <span className="font-bold text-primary">PIX</span>
                  </div>
                  <p className="text-sm text-foreground/70">Pagamento instantâneo</p>
                </div>
                
                <div
                  onClick={() => setCheckoutData({ ...checkoutData, payment_method: 'boleto' })}
                  className={`border-2 p-4 rounded-lg cursor-pointer transition-all ${
                    checkoutData.payment_method === 'boleto'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      checkoutData.payment_method === 'boleto' ? 'border-accent' : 'border-border'
                    }`}>
                      {checkoutData.payment_method === 'boleto' && (
                        <div className="w-3 h-3 rounded-full bg-accent"></div>
                      )}
                    </div>
                    <span className="font-bold text-primary">Boleto</span>
                  </div>
                  <p className="text-sm text-foreground/70">Vencimento em 3 dias</p>
                </div>

                <div
                  onClick={() => setCheckoutData({ ...checkoutData, payment_method: 'credit_card' })}
                  className={`border-2 p-4 rounded-lg cursor-pointer transition-all ${
                    checkoutData.payment_method === 'credit_card'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      checkoutData.payment_method === 'credit_card' ? 'border-accent' : 'border-border'
                    }`}>
                      {checkoutData.payment_method === 'credit_card' && (
                        <div className="w-3 h-3 rounded-full bg-accent"></div>
                      )}
                    </div>
                    <span className="font-bold text-primary">Cartão</span>
                  </div>
                  <p className="text-sm text-foreground/70">Crédito</p>
                </div>
              </div>
            </div>

            {checkoutData.payment_method === 'credit_card' && (
              <CreditCardFields
                cardForm={cardForm}
                setCardForm={setCardForm}
                totalAmount={getTotalAmount()}
              />
            )}

            <div className="bg-secondary/50 p-4 rounded">
              <p className="text-sm font-bold uppercase tracking-widest text-primary mb-2">Resumo</p>
              <div className="flex items-center justify-between">
                <span className="text-base text-foreground">Total</span>
                <span className="text-xl font-bold text-accent">R$ {getTotalAmount().toFixed(2)}</span>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="confirm-order-button"
              disabled={submitting}
              className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm"
            >
              {submitting ? 'Processando...' : 'Confirmar Pedido'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* PIX Payment Dialog */}
      <Dialog open={pixDialogOpen} onOpenChange={setPixDialogOpen}>
        <DialogContent data-testid="pix-dialog-store" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold uppercase text-primary flex items-center gap-2">
              <QrCode className="w-6 h-6" />
              Pagamento PIX
            </DialogTitle>
          </DialogHeader>
          
          {pixData && (
            <div className="space-y-4">
              <div className="text-center bg-secondary/50 p-4 rounded-lg">
                <p className="text-sm uppercase tracking-widest font-bold text-primary mb-2">Valor</p>
                <p className="text-4xl font-bold text-accent">R$ {pixData.amount.toFixed(2)}</p>
              </div>

              {pixData.qr_code_url && (
                <div className="flex justify-center bg-white p-4 rounded-lg">
                  <img src={pixData.qr_code_url} alt="QR Code PIX" className="w-64 h-64" />
                </div>
              )}

              <div className="space-y-2">
                <p className="text-sm font-bold uppercase tracking-widest text-primary">Código PIX Copia e Cola:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={pixData.pix_code}
                    readOnly
                    className="flex-1 p-3 border-2 border-border rounded-sm font-mono text-xs bg-secondary"
                  />
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(pixData.pix_code);
                      toast({ title: 'Copiado!', description: 'Código PIX copiado.' });
                    }}
                    className="bg-accent text-accent-foreground hover:bg-accent/90 px-6 rounded-sm"
                  >
                    Copiar
                  </Button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-sm font-bold text-blue-900 mb-2">Como pagar:</p>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Abra o app do seu banco</li>
                  <li>Escolha pagar via PIX</li>
                  <li>Escaneie o QR Code ou cole o código</li>
                  <li>Confirme o pagamento</li>
                </ol>
              </div>

              <p className="text-xs text-center text-foreground/50">
                Após confirmar o pagamento, você receberá um email de confirmação.
              </p>

              <Button
                onClick={() => {
                  setPixDialogOpen(false);
                  setShowThankYou(true);
                }}
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 text-lg font-bold rounded-sm mt-4"
              >
                OK, Entendi
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Boleto Payment Dialog */}
      <Dialog open={boletoDialogOpen} onOpenChange={setBoletoDialogOpen}>
        <DialogContent data-testid="boleto-dialog-store" className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-2xl font-heading font-bold uppercase text-primary flex items-center gap-2">
              <FileText className="w-6 h-6" />
              Boleto Bancário
            </DialogTitle>
          </DialogHeader>
          
          {boletoData && (
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              <div className="text-center bg-secondary/50 p-4 rounded-lg">
                <p className="text-sm uppercase tracking-widest font-bold text-primary mb-2">Valor</p>
                <p className="text-4xl font-bold text-accent">R$ {boletoData.amount?.toFixed(2)}</p>
                {boletoData.due_date && (
                  <p className="text-sm text-foreground/70 mt-2">
                    Vencimento: {new Date(boletoData.due_date).toLocaleDateString('pt-BR')}
                  </p>
                )}
              </div>

              {/* Boleto Image - with max height */}
              {boletoData.png_url && (
                <div className="flex justify-center bg-white p-4 rounded-lg border-2 border-border max-h-48 overflow-auto">
                  <img 
                    src={boletoData.png_url} 
                    alt="Boleto" 
                    className="max-w-full h-auto"
                  />
                </div>
              )}

              {/* Linha Digitável */}
              <div className="space-y-2">
                <p className="text-sm font-bold uppercase tracking-widest text-primary">Linha Digitável:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={boletoData.formatted_barcode || boletoData.barcode || ''}
                    readOnly
                    className="flex-1 p-3 border-2 border-border rounded-sm font-mono text-xs bg-secondary"
                  />
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(boletoData.formatted_barcode || boletoData.barcode);
                      toast({ title: 'Copiado!', description: 'Linha digitável copiada.' });
                    }}
                    className="bg-accent text-accent-foreground hover:bg-accent/90 px-4 rounded-sm"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Download/View Buttons */}
              <div className="flex gap-2">
                {boletoData.pdf_url && (
                  <Button
                    onClick={() => window.open(boletoData.pdf_url, '_blank')}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar PDF
                  </Button>
                )}
                {boletoData.png_url && (
                  <Button
                    onClick={() => window.open(boletoData.png_url, '_blank')}
                    variant="outline"
                    className="flex-1 rounded-sm"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Ver Imagem
                  </Button>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-sm font-bold text-yellow-900 mb-2">Como pagar:</p>
                <ol className="text-sm text-yellow-800 space-y-1 list-decimal list-inside">
                  <li>Copie a linha digitável acima</li>
                  <li>Acesse o app do seu banco ou internet banking</li>
                  <li>Escolha a opção "Pagar boleto"</li>
                  <li>Cole a linha digitável e confirme o pagamento</li>
                </ol>
              </div>

              <p className="text-xs text-center text-foreground/50">
                O boleto pode levar até 3 dias úteis para ser compensado.
                Após o pagamento, você receberá um email de confirmação.
              </p>

              <Button
                onClick={() => {
                  setBoletoDialogOpen(false);
                  setShowThankYou(true);
                }}
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 text-lg font-bold rounded-sm mt-4"
              >
                OK, Entendi
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Thank You Dialog */}
      <Dialog open={showThankYou} onOpenChange={setShowThankYou}>
        <DialogContent data-testid="thank-you-dialog-store" className="sm:max-w-md">
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-heading font-bold uppercase text-primary mb-4">
              Obrigado pelo seu pedido!
            </h2>
            
            <p className="text-foreground/70 mb-4">
              Seu pedido foi realizado com sucesso.
            </p>
            
            {orderId && (
              <div className="bg-secondary/50 p-4 rounded-lg mb-6">
                <p className="text-sm uppercase tracking-widest font-bold text-primary mb-1">Nº do Pedido</p>
                <p className="text-lg font-mono font-bold">{orderId.slice(0, 8).toUpperCase()}</p>
              </div>
            )}
            
            <p className="text-sm text-foreground/60 mb-6">
              Um email de confirmação foi enviado com todos os detalhes do seu pedido.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowThankYou(false);
                }}
                className="flex-1 rounded-sm"
              >
                Continuar Comprando
              </Button>
              <Button
                onClick={() => {
                  setShowThankYou(false);
                  navigate('/');
                }}
                className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm"
              >
                Ir para Home
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Store;
